import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './dicomService';

export type LabelKey = 'view_label' | 'quality_label' | 'segmentation' | 'keypoints' | 'view_prediction';

export interface ExploreFilters {
  q?: string;
  datasource?: string;
  manufacturer?: string;
  model?: string;
  type?: string;
  year?: string;
  view?: string;
  has_report?: 'true' | 'false';
  label?: LabelKey[];
  text?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

export interface MediaUrls {
  poster: string | null;
  cine: string | null;
  cine_alt?: string | null;
  still: string | null;
}

export interface Overview {
  generated_at: string;
  totals: {
    studies: number;
    dicoms: number;
    patients: number;
    datasources: number;
    studies_with_report: number;
  };
  label_coverage: { key: LabelKey; label: string; dicoms: number }[];
  view_agreement: { both: number; agree: number };
  by_datasource: { id: string | null; name: string; studies: number; dicoms: number }[];
  by_manufacturer: { manufacturer: string; model: string; dicoms: number }[];
  by_type: { type: string; dicoms: number }[];
  by_year: { year: string; studies: number }[];
  by_view: { view: string; predicted: number; labeled: number }[];
}

export interface FacetOption {
  value: string;
  label?: string;
  n?: number;
  manufacturer?: string;
}

export interface Facets {
  datasources: FacetOption[];
  manufacturers: FacetOption[];
  models: FacetOption[];
  types: FacetOption[];
  years: FacetOption[];
  views: FacetOption[];
}

export interface StudyRow {
  uuid: string;
  icid: string | null;
  foreign_id: string | null;
  visit_year: string | null;
  age_at_visit: string | null;
  designation: string | null;
  number_of_dicoms: number | null;
  number_of_usable_dicoms: number | null;
  created_at: string | null;
  datasource: string | null;
  gender: string | null;
  has_report: boolean;
  stats: {
    dicoms: number;
    manufacturers: string[];
    types: string[];
    view_label: number;
    segmentation: number;
    keypoints: number;
    view_prediction: number;
  };
}

export interface DicomRow {
  uuid: string;
  icid: string;
  study_id: string;
  study_icid: string | null;
  index: number | null;
  manufacturer: string | null;
  model: string | null;
  type: string | null;
  number_of_frames: number | null;
  frames_per_second: number | null;
  pixel_width: number | null;
  pixel_height: number | null;
  created_at: string | null;
  skipped_annotations: boolean | null;
  predicted_view: string | null;
  predicted_confidence: number | null;
  labeled_view: string | null;
  has_segmentation: boolean;
  has_keypoints: boolean;
  has_quality_label: boolean;
  media: MediaUrls;
}

export interface Page<T> {
  total: number;
  offset: number;
  limit: number;
  items: T[];
}

export interface StudyDetail {
  study: {
    uuid: string;
    icid: string | null;
    foreign_id: string | null;
    visit_year: string | null;
    age_at_visit: string | null;
    designation: string | null;
    number_of_dicoms: number | null;
    number_of_usable_dicoms: number | null;
    skip_ef: boolean | null;
    created_at: string | null;
    datasource_id: string | null;
    datasource: string | null;
    gender: string | null;
    race: string | null;
    ethnicity: string | null;
  };
  report: null | ReportSummary;
  dicoms: DicomRow[];
}

export interface ReportSummary {
  uuid: string;
  icid: string | null;
  signer: string | null;
  date_created: string | null;
  measurements: { key: string; value: string | number; unit: string | null }[];
  sections: { key: string; text: string }[];
  findings: {
    text: string | null;
    description: string | null;
    region: string | null;
    condition: string | null;
    stratification: string | null;
  }[];
}

export interface SearchResult {
  query: string;
  tokens: string[];
  totals: Partial<{
    reports: number;
    reports_coded: number;
    reports_text: number;
    studies: number;
    dicoms: number;
    patients: number;
  }>;
  by_characterization: { region: string; condition: string; stratification: string; reports: number; studies: number }[];
  by_source: { id: string | null; name: string; studies: number }[];
  studies: {
    uuid: string;
    icid: string | null;
    visit_year: string | null;
    age_at_visit: string | null;
    datasource: string | null;
    number_of_dicoms: number | null;
    via: ('coded' | 'finding' | 'text')[];
    characterizations: string[];
    snippet: string | null;
  }[];
  limit?: number;
}

/** Serialise filters to a query string (repeated `label` params, empty values dropped). */
export function toQuery(f: ExploreFilters): string {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) v.forEach(x => p.append(k, String(x)));
    else p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const exploreApi = createApi({
  reducerPath: 'exploreApi',
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: 300,
  endpoints: builder => ({
    getOverview: builder.query<Overview, void>({
      query: () => '/api/v1/explore/overview',
    }),
    getFacets: builder.query<Facets, void>({
      query: () => '/api/v1/explore/facets',
    }),
    getStudies: builder.query<Page<StudyRow>, ExploreFilters>({
      query: f => `/api/v1/explore/studies${toQuery(f)}`,
    }),
    getStudyDetail: builder.query<StudyDetail, string>({
      query: key => `/api/v1/explore/studies/${encodeURIComponent(key)}`,
    }),
    getDicoms: builder.query<Page<DicomRow>, ExploreFilters>({
      query: f => `/api/v1/explore/dicoms${toQuery(f)}`,
    }),
    randomStudy: builder.mutation<{ icid: string }, ExploreFilters | void>({
      query: f => `/api/v1/explore/studies/random${toQuery(f || {})}`,
    }),
    searchReports: builder.query<SearchResult, string>({
      query: q => `/api/v1/explore/search?q=${encodeURIComponent(q)}`,
    }),
  }),
});

export const {
  useGetOverviewQuery,
  useGetFacetsQuery,
  useGetStudiesQuery,
  useGetStudyDetailQuery,
  useGetDicomsQuery,
  useSearchReportsQuery,
  useRandomStudyMutation,
} = exploreApi;
